import { createTheme } from '@mui/material/styles';

export const getTheme = (options = {}) => {
  const {
    darkMode = false,
    customColors = {},
    fontFamily = 'Inter, Arial, sans-serif',
    density = 'normal'
  } = options;

  const isLight = !darkMode;
  const spacingMap = {
    compact: 4,
    normal: 8,
    comfortable: 10
  };

  return createTheme({
    palette: {
      mode: darkMode ? 'dark' : 'light',
      primary: {
        main: customColors.primary || (isLight ? '#7b2a2a' : '#cb534f'),
        ...(isLight && { dark: '#662323' })
      },
      secondary: {
        main: customColors.secondary || '#646aff',
      },
      success: {
        main: isLight ? '#2e7d32' : '#4caf50', // Added success color
        contrastText: '#fff'
      },
      error: {
        main: isLight ? '#d32f2f' : '#ef5350', // Added error color
        contrastText: '#fff'
      },
      warning: {
        main: isLight ? '#ed6c02' : '#ff9800', // Added warning color
        contrastText: '#fff'
      },
      info: {
        main: isLight ? '#0288d1' : '#03a9f4', // Added info color
        contrastText: '#fff'
      },
      background: {
        default: isLight ? '#ececec' : '#121212',
      },
      disabled: {
        main: isLight ? 'rgba(0, 0, 0, 0.26)' : 'rgba(255, 255, 255, 0.3)',
        light: isLight ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.12)',
        dark: isLight ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.12)',
        contrastText: isLight ? 'rgba(0, 0, 0, 0.26)' : 'rgba(255, 255, 255, 0.3)',
      },
    },
    spacing: spacingMap[density] || 8,
    components: {
      MuiAppBar: {
        styleOverrides: {
          root: ({ theme }) => ({
            paddingTop: 'env(safe-area-inset-top)',
            height: `calc(${theme.mixins.toolbar.minHeight}px + env(safe-area-inset-top))`,
            boxSizing: 'border-box',
          }),
          colorPrimary: {
            backgroundColor: '#fff',
            backgroundImage: 'unset',
            color: '#000'
          }
        }
      },
      MuiButton: {
        defaultProps: {
          variant: 'outlined',
        },
        styleOverrides: {
          root: {
            fontWeight: 'bold',
          },
        }
      },
      MuiListItemText: {
        styleOverrides: {
          primary: {
            fontWeight: 'bold',
          }
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            fontWeight: 'bold',
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: {
            '& .MuiTab-root': {
              fontWeight: 'bold',
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            fontWeight: 'bold',
          },
          input: {
            fontWeight: 'bold',
          },
          inputMultiline: {
            fontWeight: 'normal',
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          root: {
            fontWeight: 'bold',
          },
          select: {
            fontWeight: 'bold',
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          head: {
            fontWeight: 'bold',
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          input: {
            colorScheme: isLight ? 'light' : 'dark',
          }
        }
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            fontWeight: 'bold'
          }
        }
      },
      MuiFormControlLabel: {
        styleOverrides: {
          label: {
            fontWeight: 'bold'
          }
        }
      },
      MuiCssBaseline: {
        styleOverrides: {
          ':root': {
            '--safe-top': 'env(safe-area-inset-top)',
            '--safe-bottom': 'env(safe-area-inset-bottom)'
          },
          body: {
            paddingTop: 'var(--safe-top)',
            paddingBottom: 'var(--safe-bottom)',
            scrollbarColor: `${darkMode ? 'rgba(255,255,255,.3)' : 'rgba(0,0,0,.26)'} transparent`,
            colorScheme: isLight ? 'light' : 'dark'
          },
          '*': {
            scrollbarColor: `${darkMode ? 'rgba(255,255,255,.3)' : 'rgba(0,0,0,.26)'} transparent`,
            colorScheme: isLight ? 'light' : 'dark'
          }
        }
      }
    },
    shape: {
      borderRadius: 12,
    },
    typography: {
      fontFamily,
      h1: { fontWeight: 'bold' },
      h2: { fontWeight: 'bold' },
      h3: { fontWeight: 'bold' },
      h4: { fontWeight: 'bold' },
      h5: { fontWeight: 'bold' },
      h6: { fontWeight: 'bold' },
      codeblock: {
        fontFamily: '"Source Code Pro", monospace',
        fontSize: '0.875rem',
        fontWeight: 'normal',
      },
    },
  });
};

export const lightTheme = getTheme({ darkMode: false });
export const darkTheme = getTheme({ darkMode: true });